import React from "react";
import { PackagePayload, isModelPackage, primaryCategory } from "../../services/packages";
import { CatalogItemStripHeader } from "../../components/catalog/CatalogKindVisuals";
import {
  modelLabelCount,
  modelRuntimeLabel,
  modelSizeLabel,
  modelTypeLabel,
} from "../../components/models/catalog/modelFacts";
import browseStyles from "./CatalogBrowseLayout.module.css";
import styles from "./PackageBrowseCard.module.css";
import { countLabel } from "../../utils/countLabel";

/**
 * The card's colour is its node category — the same palette the canvas paints a
 * node's left border with, and the same one behind the category chips in the
 * filter bar. It used to be a character-sum hash of `dirName`, so the colour
 * told you nothing: three `data` packages rendered orange, violet and blue,
 * and a `computation` package shared the blue.
 */
function stripClass(styleMap: Record<string, string>, prefix: string, category: string): string {
  return styleMap[`${prefix}_${category}`] ?? styleMap[`${prefix}_package`] ?? "";
}

function relativeFromMs(ms?: number): string {
  if (ms == null || ms <= 0) return "—";
  const delta = Date.now() - ms;
  if (!Number.isFinite(delta)) return "—";
  const minutes = Math.max(1, Math.round(delta / 60_000));
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export interface PackageBrowseCardProps {
  pkg: PackagePayload;
  selected: boolean;
  isInstalled: boolean;
  hasUpdate: boolean;
  catalogRow: PackagePayload | undefined;
  onSelect: () => void;
  onViewDetails?: () => void;
  /** Right-click. The grid owns the menu, so the card only reports the event
   *  and selects itself - the same thing a left-click does. */
  onContextMenu?: (e: React.MouseEvent) => void;
}

export const PackageBrowseCard: React.FC<PackageBrowseCardProps> = ({
  pkg,
  selected,
  isInstalled,
  hasUpdate,
  catalogRow,
  onSelect,
  onViewDetails,
  onContextMenu,
}) => {
  const cat = primaryCategory(pkg);
  const cardStyles = styles as Record<string, string>;
  // A model package is the Model Catalog's card: the model kind's colour, its
  // runtime as the badge, its type and labels as chips, and its weights'
  // license where a node package shows its age.
  const model = isModelPackage(pkg) ? pkg : null;
  const colourKey = model ? "model" : cat;

  return (
    <article
      className={[
        browseStyles.card,
        selected ? browseStyles.cardActive : "",
        selected ? styles.cardActive : "",
        stripClass(cardStyles, "card", colourKey),
      ].join(" ")}
      // Same cross-surface identity attribute the drawer's PackageCard carries.
      data-pkg-dir={pkg.dirName}
      {...(model ? { "data-model-id": model.packageId } : {})}
      role="button"
      tabIndex={0}
      onContextMenu={onContextMenu}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onSelect();
      }}
    >
      <div className={`${browseStyles.cardStrip} ${stripClass(cardStyles, "strip", colourKey)}`}>
        <CatalogItemStripHeader
          kind={model ? "model" : "package"}
          badge={
            <span className={browseStyles.cardFormatBadge}>{model ? modelRuntimeLabel(model) : cat}</span>
          }
        />
      </div>

      <div className={browseStyles.cardBody}>
        <h2 className={browseStyles.cardTitle}>{pkg.name}</h2>
        <p className={browseStyles.publisher}>
          {pkg.publisher || pkg.packageId} · v{pkg.version}
          {pkg.license && !model ? ` · ${pkg.license}` : ""}
        </p>
        <p
          className={browseStyles.cardDescription}
          {...(!pkg.description ? { "aria-hidden": true } : {})}
        >
          {pkg.description || "\u00a0"}
        </p>
        {/* The weights' license, in full on a line of its own, above the
            chips, which keep their place at the foot of the body. */}
        {model ? (
          <p className={styles.weightsLicense} data-tooltip="Weights license" data-curio-weights-license="true">
            {model.model.license || "Unknown license"}
          </p>
        ) : null}
        {/* See DataCatalogBrowseCard: aims the #333 baseline at the claim. */}
        <div className={browseStyles.tagRow} data-curio-tag-row="true">
          {model ? (
            <span className={browseStyles.tag} data-curio-tag-chip="true">{modelTypeLabel(model)}</span>
          ) : (
            <>
              <span className={browseStyles.tag} data-curio-tag-chip="true">
                {countLabel(pkg.templates.length, "node")}
              </span>
              <span className={browseStyles.tag} data-curio-tag-chip="true">{cat}</span>
            </>
          )}
          {(pkg.channel ?? "stable") !== "stable" ? (
            <span className={browseStyles.tag} data-curio-tag-chip="true">{pkg.channel}</span>
          ) : null}
          {hasUpdate && catalogRow ? (
            <span className={`${browseStyles.tag} ${styles.tagUpdate}`}>
              Update to {catalogRow.version}
            </span>
          ) : null}
        </div>
      </div>

      {model ? (
        <div className={browseStyles.cardMeta}>
          <span className={browseStyles.metaLeft}>
            {[model.model.labels.length > 0 ? modelLabelCount(model) : null, modelSizeLabel(model)]
              .filter(Boolean)
              .join(" · ")}
          </span>
          <span className={browseStyles.metaRight}>{relativeFromMs(pkg.createdAtMs)}</span>
        </div>
      ) : (
        <div className={browseStyles.cardMeta}>
          <span className={browseStyles.metaLeft}>
            {countLabel(pkg.templates.length, "template")} · {pkg.packageId}
          </span>
          <span className={browseStyles.metaRight}>{relativeFromMs(pkg.createdAtMs)}</span>
        </div>
      )}

      <div className={browseStyles.cardActions}>
        {/* Identity and one way in. Publishing is an account-level decision
            about one package and belongs in the detail drawer beside the other
            decisions, not on every tile in the grid. */}
        <div className={browseStyles.cardActionsLeft}>
          {isInstalled ? <span className={browseStyles.cardStatus}>✓ In all projects</span> : null}
        </div>
        <div className={browseStyles.cardActionsRight}>
          <button
            className={browseStyles.linkButton}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              // The modal, not the drawer. This used to call `onSelect`, so on
              // a card whose drawer was already open the click did nothing.
              (onViewDetails ?? onSelect)();
            }}
          >
            View details
          </button>
        </div>
      </div>
    </article>
  );
};
